import React from 'react';
import PropTypes from 'prop-types';
import {PivotData, makeNumbers} from './Utilities';
import i18n from './i18n';

// helper function for setting row/col-span in pivotTableRenderer
const spanSize = function(arr, i, j) {
  let x;
  if (i !== 0) {
    let asc, end;
    let noDraw = true;
    for (
      x = 0, end = j, asc = end >= 0;
      asc ? x <= end : x >= end;
      asc ? x++ : x--
    ) {
      if (arr[i - 1][x] !== arr[i][x]) {
        noDraw = false;
      }
    }
    if (noDraw) {
      return -1;
    }
  }
  let len = 0;
  while (i + len < arr.length) {
    let asc1, end1;
    let stop = false;
    for (
      x = 0, end1 = j, asc1 = end1 >= 0;
      asc1 ? x <= end1 : x >= end1;
      asc1 ? x++ : x--
    ) {
      if (arr[i][x] !== arr[i + len][x]) {
        stop = true;
      }
    }
    if (stop) {
      break;
    }
    len++;
  }
  return len;
};

function redColorScaleGenerator(values) {
  const min = Math.min.apply(Math, values);
  const max = Math.max.apply(Math, values);
  return x => {
    // eslint-disable-next-line no-magic-numbers
    const nonRed = 255 - Math.round((255 * (x - min)) / (max - min));
    return {backgroundColor: `rgb(255,${nonRed},${nonRed})`};
  };
}

const SCENARIOS = {
  ROW_AND_COLUMN: 1,
  ONLY_ROW: 2,
  ONLY_COLUMN: 3,
  ONLY_METRICS: 4,
};
/**
 * Cenários: TODOS POSSUEM MÉTRICAS (aggAttrs)
 * 01 - 0 atributos na linha e 0 atributos na coluna
 * 02 - N atributos na linha e 0 atributos na coluna
 * 03 - 0 atributos na linha e N atributos na coluna
 * 04 - N atributos na linha e N atributos na coluna
 */
const getSpanState = (rowAttrs, colAttrs, aggAttrs) => {
  const ROW_AND_COLUMN = rowAttrs.length > 0 && colAttrs.length > 0;
  const ONLY_ROW = rowAttrs.length > 0 && colAttrs.length === 0;
  const ONLY_COLUMN = rowAttrs.length === 0 && colAttrs.length > 0;
  const ONLY_METRICS = rowAttrs.length === 0 && colAttrs.length === 0;
  const defaultState = {
    aggSize: aggAttrs.length,
    scenario:
      (ROW_AND_COLUMN && SCENARIOS.ROW_AND_COLUMN) ||
      (ONLY_ROW && SCENARIOS.ONLY_ROW) ||
      (ONLY_COLUMN && SCENARIOS.ONLY_COLUMN) ||
      SCENARIOS.ONLY_METRICS,
    tHead: {
      colSpan: rowAttrs.length,
      rowSpan: colAttrs.length,
      axisLabelLastColRowSpan: 1,
      axisLabelRowSpan: 1,
      colLabelRowSpan: j =>
        j === colAttrs.length - 1 && rowAttrs.length !== 0 ? 2 : 1,
    },
    pvtAxisLabel: {
      rowSpan: 1,
      colSpan: 1,
    },
    pvtTotalLabel: {
      colSpan: aggAttrs.length,
      rowSpan: colAttrs.length + (rowAttrs.length === 0 ? 0 : 1),
      lastRowSpan: !ONLY_COLUMN ? 1 : 2,
      lastColSpan: 1,
      aggListRowSpan: !ONLY_COLUMN ? 1 : 2,
      aggListColSpan: 1,
    },
  };
  if (ROW_AND_COLUMN) {
    return {
      ...defaultState,
      tHead: {
        ...defaultState.tHead,
        axisLabelLastColRowSpan: 1,
        colLabelRowSpan: _ => 1,
      },
      pvtTotalLabel: {
        ...defaultState.pvtTotalLabel,
        rowSpan: colAttrs.length,
      },
    };
  }
  if (ONLY_ROW) {
    return {
      ...defaultState,
      tHead: {...defaultState.tHead, colLabelRowSpan: _ => 1},
      pvtTotalLabel: {
        ...defaultState.pvtTotalLabel,
        rowSpan: 1,
        lastRowSpan: 1,
        lastColSpan: aggAttrs.length,
        aggListRowSpan: 2,
        aggListColSpan: 1,
      },
    };
  }
  if (ONLY_COLUMN) {
    return {
      ...defaultState, tHead: {...defaultState.tHead, axisLabelLastColRowSpan: 2},
      pvtAxisLabel: {
        ...defaultState.pvtAxisLabel,
        rowSpan: 2,
      },
      pvtTotalLabel: {
        ...defaultState.pvtTotalLabel,
        lastRowSpan: aggAttrs.length,
        aggListRowSpan: 1,
        aggListColSpan: 1,
      },
    };
  }
  if (ONLY_METRICS) {
    return {
      ...defaultState,
      tHead: {...defaultState.tHead, axisLabelLastColRowSpan: colAttrs.length},
      pvtAxisLabel: {
        ...defaultState.pvtAxisLabel,
        rowSpan: colAttrs.length,
      },
      pvtTotalLabel: {
        ...defaultState.pvtTotalLabel,
        lastRowSpan: aggAttrs.length,
      },
    };
  }
};

const THeadAgg = ({colKeys, rowAttrs, colAttrs, aggAttrs, aggTitles}) => {
  const spanState = getSpanState(rowAttrs, colAttrs, aggAttrs);

  const parte1 = (
    <>
      {rowAttrs.length !== 0 && (
        <>
          {rowAttrs.map(function(r, i) {
            return (
              <th
                className="pvtAxisLabel"
                rowSpan={spanState.pvtTotalLabel.aggListRowSpan}
                key={`rowAttr${i}`}
              >
                {r}
              </th>
            );
          })}
          {/* Variáveis apenas na linha */}
          <th
            className="pvtTotalLabel"
            rowSpan={spanState.pvtTotalLabel.lastRowSpan}
            colSpan={spanState.pvtTotalLabel.lastColSpan}
          >
            {colAttrs.length === 0 ? i18n.getMessage('Totals') : null}
          </th>
        </>
      )}
      {/* Em branco para 0 linhas e 0 colunas */}
      {rowAttrs.length === 0 && colAttrs.length === 0 && <th></th>}
    </>
  );

  const parte2 = (
    <>
      {/* Cabeçalho para os AggList */}
      {colKeys.map((colKey, i) =>
        aggAttrs.map((a, ia) => (
          <th
            className="pvtColLabel"
            key={`AggColKey${i}${ia}`}
            colSpan={spanState.pvtTotalLabel.aggListColSpan}
            rowSpan={spanState.pvtTotalLabel.aggListRowSpan}
          >
            {aggTitles[ia]}
          </th>
        ))
      )}
      {/* Totals AggList */}
      {aggAttrs.map((a, i) => (
        <th
          className="pvtColLabel"
          key={`AggColKeyN${i}`}
          colSpan={spanState.pvtTotalLabel.aggListColSpan}
          rowSpan={spanState.pvtTotalLabel.lastRowSpan}
        >
          {aggTitles[i]}
        </th>
      ))}
    </>
  );

  return (
    <thead>
      {colAttrs.map(function(c, j) {
        return (
          // Cabeçalho do lado da coluna
          <tr key={`colAttr${j}`}>
            {/* Espaços em branco reservado para Linhas */}
            {j === 0 && rowAttrs.length !== 0 && (
              <th
                colSpan={spanState.tHead.colSpan}
                rowSpan={spanState.tHead.rowSpan}
              />
            )}

            {/* Nome do atributo Coluna */}
            <th
              className="pvtAxisLabel"
              rowSpan={
                colAttrs.length === j + 1
                  ? spanState.tHead.axisLabelLastColRowSpan
                  : spanState.tHead.axisLabelRowSpan
              }
            >
              {c}
            </th>

            {/* Valores da coluna */}
            {colKeys.map(function(colKey, i) {
              const x = spanSize(colKeys, i, j); // Cálcula o tamanha da coluna
              if (x === -1) {
                return null;
              }
              return (
                // Último
                <th
                  className="pvtColLabel"
                  key={`colKey${i}`}
                  colSpan={x * spanState.aggSize}
                  rowSpan={spanState.tHead.colLabelRowSpan(j)}
                >
                  {colKey[j]}
                </th>
              );
            })}

            {/* Totals: Coluna N, Linha 1 */}
            {j === 0 && (
              <th
                className="pvtTotalLabel"
                rowSpan={spanState.pvtTotalLabel.rowSpan}
                colSpan={spanState.pvtTotalLabel.colSpan}
              >
                {i18n.getMessage('Totals')}
              </th>
            )}
          </tr>
        );
      })}

      {/* Cabeçalho do lado da linha */}
      {spanState.scenario === SCENARIOS.ONLY_ROW ? (
        <>
          <tr>{parte1}</tr>
          <tr>{parte2}</tr>
        </>
      ) : (
        <tr>
          {parte1}
          {parte2}
        </tr>
      )}
    </thead>
  );
};

const TBodyAgg = ({
  rowKeys,
  colKeys,
  pivotData,
  rowAttrs,
  colAttrs,
  getClickHandler,
  rowTotalColors,
  valueCellColors,
  colTotalColors,
  grandTotalAggregatorList,
}) => {
  return (
    <tbody>
      {rowKeys.map(function(rowKey, i) {
        const totalAggregatorList = pivotData.getAggregatorList(rowKey, []);
        return (
          <tr key={`rowKeyRow${i}`}>
            {rowKey.map(function(txt, j) {
              const x = spanSize(rowKeys, i, j);
              if (x === -1) {
                return null;
              }
              return (
                <th
                  key={`rowKeyLabel${i}-${j}`}
                  className="pvtRowLabel"
                  rowSpan={x}
                  colSpan={
                    j === rowAttrs.length - 1 && colAttrs.length !== 0 ? 2 : 1
                  }
                >
                  <div dangerouslySetInnerHTML={{__html: txt}} />
                </th>
              );
            })}
            {colKeys.map(function(colKey, j) {
              const aggregatorList = pivotData.getAggregatorList(
                rowKey,
                colKey
              );
              return aggregatorList?.map((agg, ai) => (
                <td
                  className="pvtVal"
                  key={`pvtVal${i}-${j}-${ai}`}
                  onClick={
                    getClickHandler &&
                    getClickHandler(agg.value(ai), rowKey, colKey)
                  }
                  style={valueCellColors(rowKey, colKey, agg.value(ai))}
                >
                  {agg.format(agg.value(ai))}
                </td>
              ));
            })}
            {/* Totals [1 n] x [N N] (Totais na coluna) */}
            {totalAggregatorList?.map((totalAgg, ai) => (
              <td
                key={`pvtTotal${i}-${ai}`}
                className="pvtTotal"
                onClick={
                  getClickHandler &&
                  getClickHandler(totalAgg.value(ai), rowKey, [null])
                }
                style={colTotalColors(totalAgg.value(ai))}
              >
                {totalAgg.format(totalAgg.value(ai))}
              </td>
            ))}
          </tr>
        );
      })}

      {/* Última linha */}
      <tr>
        {/* Total Linha */}
        <th
          className="pvtTotalLabel"
          colSpan={rowAttrs.length + (colAttrs.length === 0 ? 0 : 1)}
        >
          {i18n.getMessage('Totals')}
        </th>

        {colKeys.map(function(colKey, i) {
          const totalAggregatorList = pivotData.getAggregatorList([], colKey);
          return totalAggregatorList?.map((totalAgg, ia) => (
            <td
              className="pvtTotal"
              key={`total${i}-${ia}`}
              onClick={
                getClickHandler &&
                getClickHandler(totalAgg.value(ia), [null], colKey)
              }
              style={rowTotalColors(totalAgg.value(ia))}
            >
              {totalAgg.format(totalAgg.value(ia))}
            </td>
          ));
        })}

        {/* Total cruzamento */}
        {/* Valor Total do Cruzamento */}
        {grandTotalAggregatorList?.map((grandTotalAgg, ia) => (
          <td
            key={`grandTotalAgg${ia}`}
            onClick={
              getClickHandler &&
              getClickHandler(grandTotalAgg.value(ia), [null], [null])
            }
            className="pvtGrandTotal"
          >
            {grandTotalAgg.format(grandTotalAgg.value(ia))}
          </td>
        ))}
      </tr>
    </tbody>
  );
};

const TBodyAggWithPer = ({
  rowKeys,
  colKeys,
  pivotData,
  rowAttrs,
  colAttrs,
  getClickHandler,
  rowTotalColors,
  valueCellColors,
  colTotalColors,
  grandTotalAggregatorList,
}) => {
  return (
    <tbody className="percentage">
      {rowKeys.map(function(rowKey, i) {
        const totalAggregatorList = pivotData.getAggregatorList(rowKey, []);
        return (
          <tr key={`rowKeyRow${i}`}>
            {rowKey.map(function(txt, j) {
              const x = spanSize(rowKeys, i, j);
              if (x === -1) {
                return null;
              }
              return (
                <th
                  key={`rowKeyLabel${i}-${j}`}
                  className="pvtRowLabel"
                  rowSpan={x}
                  colSpan={
                    j === rowAttrs.length - 1 && colAttrs.length !== 0 ? 2 : 1
                  }
                >
                  <div dangerouslySetInnerHTML={{__html: txt}} />
                </th>
              );
            })}
            {colKeys.map(function(colKey, j) {
              const aggregatorList = pivotData.getAggregatorList(
                rowKey,
                colKey
              );
              return aggregatorList?.map((agg, ai) =>
                ai === 0 ? ( // L: Adcionei para renderizar apenas os números e não a porcentagem na tabela
                  <td
                    className="pvtVal"
                    key={`pvtVal${i}-${j}-${ai}`}
                    colSpan={2}
                    onClick={
                      getClickHandler &&
                      getClickHandler(agg.value(ai), rowKey, colKey)
                    }
                    style={valueCellColors(rowKey, colKey, agg.value(ai))}
                  >
                    {agg.format(agg.value(ai))}
                  </td>
                ) : null
              );
            })}
            {/* Totals [1 n] x [N N] (Totais na coluna) */}
            {totalAggregatorList?.map((totalAgg, ai) => (
              <td
                key={`pvtTotal${i}-${ai}`}
                className="pvtTotal"
                onClick={
                  getClickHandler &&
                  getClickHandler(totalAgg.value(ai), rowKey, [null])
                }
                style={colTotalColors(totalAgg.value(ai))}
              >
                {totalAgg.format(totalAgg.value(ai))}
              </td>
            ))}
          </tr>
        );
      })}

      {/* Última linha */}
      <tr>
        {/* Total Linha */}
        <th
          className="pvtTotalLabel"
          colSpan={rowAttrs.length + (colAttrs.length === 0 ? 0 : 1)}
        >
          {i18n.getMessage('Totals')}
        </th>

        {colKeys.map(function(colKey, i) {
          const totalAggregatorList = pivotData.getAggregatorList([], colKey);
          return totalAggregatorList?.map((totalAgg, ia) =>
            ia === 0 ? (
              <td
                className="pvtTotal"
                key={`total${i}-${ia}`}
                colSpan={2}
                onClick={
                  getClickHandler &&
                  getClickHandler(totalAgg.value(ia), [null], colKey)
                }
                style={rowTotalColors(totalAgg.value(ia))}
                // style={{backgroundColor: "#ffe4c4"}} // L: não faço ideia
              >
                {totalAgg.format(totalAgg.value(ia))}
              </td>
            ) : null
          );
        })}

        {/* Total cruzamento */}
        {/* Valor Total do Cruzamento */}
        {grandTotalAggregatorList?.map((grandTotalAgg, ia) => (
          <td
            key={`grandTotalAgg${ia}`}
            onClick={
              getClickHandler &&
              getClickHandler(grandTotalAgg.value(ia), [null], [null])
            }
            className="pvtGrandTotal"
          >
            {grandTotalAgg.format(grandTotalAgg.value(ia))}
          </td>
        ))}
      </tr>
      {pivotData.getAggAttrs()[1].map((aggTitle, i) => {
        // L: adcionei para renderizar o % em baixo no tbodyagg
        return i === 0 ? null : (
          <tr>
            {/* Total Linha */}
            <th
              className="pvtTotalLabel"
              colSpan={rowAttrs.length + (colAttrs.length === 0 ? 0 : 1)}
            >
              {aggTitle}
            </th>

            {colKeys.map(function(colKey, i) {
              const totalAggregatorList = pivotData.getAggregatorList(
                [],
                colKey
              );
              return totalAggregatorList?.map((totalAgg, ia) =>
                ia !== 0 ? (
                  <td
                    className="pvtTotal"
                    colSpan={2}
                    key={`total${i}-${ia}`}
                    onClick={
                      getClickHandler &&
                      getClickHandler(totalAgg.value(ia), [null], colKey)
                    }
                    style={rowTotalColors(totalAgg.value(ia))}
                  >
                    {totalAgg.format(totalAgg.value(ia))}
                  </td>
                ) : null
              );
            })}

            {/* Total cruzamento */}
            {/* Valor Total do Cruzamento */}
            {grandTotalAggregatorList?.map((grandTotalAgg, ia) =>
              ia !== 0 ? (
                <td
                  key={`grandTotalAgg${ia}`}
                  onClick={
                    getClickHandler &&
                    getClickHandler(grandTotalAgg.value(ia), [null], [null])
                  }
                  className="pvtGrandTotal"
                >
                  {grandTotalAgg.format(grandTotalAgg.value(ia))}
                </td>
              ) : null
            )}
          </tr>
        );
      })}
    </tbody>
  );
};

const THeadAggWithPer = ({
  colKeys,
  rowAttrs,
  colAttrs,
  aggAttrs,
  aggTitles,
}) => {
  const spanState = getSpanState(rowAttrs, colAttrs, aggAttrs);

  const parte1 = (
    <>
      {rowAttrs.length !== 0 && (
        <>
          {rowAttrs.map(function(r, i) {
            return (
              <th
                className="pvtAxisLabel"
                rowSpan={spanState.pvtTotalLabel.aggListRowSpan}
                key={`rowAttr${i}`}
              >
                {r}
              </th>
            );
          })}
          {/* Variáveis apenas na linha */}
          <th
            className="pvtTotalLabel"
            rowSpan={spanState.pvtTotalLabel.lastRowSpan}
            colSpan={spanState.pvtTotalLabel.lastColSpan}
          >
            {colAttrs.length === 0 ? i18n.getMessage('Totals') : null}
          </th>
        </>
      )}
      {/* Em branco para 0 linhas e 0 colunas */}
      {rowAttrs.length === 0 && colAttrs.length === 0 && <th></th>}
    </>
  );

  const parte2 = (
    <>
      {/* Cabeçalho para os AggList */}
      {/* {colKeys.map((colKey, i) =>
        aggAttrs.map((a, ia) =>
          ia === 0 ? ( // L: adcionei para não renderizar o % e somente o primeiro agg
            <th
              className="pvtColLabel"
              key={`AggColKey${i}${ia}`}
              colSpan={spanState.pvtTotalLabel.aggListColSpan + 1} // L: adcionei o +1 para encaixar com o colSpan
              rowSpan={spanState.pvtTotalLabel.aggListRowSpan}
            >
              {aggTitles[ia]}
            </th>
          ) : null
        )
      )} */}
      {/* Totals AggList */}
      {aggAttrs.map((a, i) => (
        <th
          className="pvtColLabel"
          key={`AggColKeyN${i}`}
          colSpan={spanState.pvtTotalLabel.aggListColSpan}
          rowSpan={spanState.pvtTotalLabel.lastRowSpan}
        >
          {aggTitles[i]}
        </th>
      ))}
    </>
  );

  return (
    <thead>
      {colAttrs.map(function(c, j) {
        return (
          // Cabeçalho do lado da coluna
          <tr key={`colAttr${j}`}>
            {/* Espaços em branco reservado para Linhas */}
            {j === 0 && rowAttrs.length !== 0 && (
              <th
                colSpan={spanState.tHead.colSpan}
                rowSpan={spanState.tHead.rowSpan}
              />
            )}

            {/* Nome do atributo Coluna */}
            <th
              className="pvtAxisLabel"
              rowSpan={
                (colAttrs.length === j + 1
                  ? spanState.tHead.axisLabelLastColRowSpan
                  : spanState.tHead.axisLabelRowSpan) + 0
              }
            >
              {c}
            </th>

            {/* Valores da coluna */}
            {colKeys.map(function(colKey, i) {
              const x = spanSize(colKeys, i, j); // Cálcula o tamanha da coluna
              if (x === -1) {
                return null;
              }
              return (
                // Último
                <th
                  className="pvtColLabel"
                  key={`colKey${i}`}
                  colSpan={x * spanState.aggSize}
                  rowSpan={spanState.tHead.colLabelRowSpan(j) + 1}
                >
                  {colKey[j]}
                </th>
              );
            })}

            {/* Totals: Coluna N, Linha 1 */}
            {j === 0 && (
              <th
                className="pvtTotalLabel"
                rowSpan={spanState.pvtTotalLabel.rowSpan}
                colSpan={spanState.pvtTotalLabel.colSpan}
              >
                {/* {i18n.getMessage('Totals')} */}
              </th>
            )}
          </tr>
        );
      })}

      {/* Cabeçalho do lado da linha */}
      {spanState.scenario === SCENARIOS.ONLY_ROW ? (
        <>
          <tr>{parte1}</tr>
          <tr>{parte2}</tr>
        </>
      ) : (
        <tr>
          {parte1}
          {parte2}
        </tr>
      )}
    </thead>
  );
};

const THead = ({colKeys, rowAttrs, colAttrs}) => (
  <thead>
    {colAttrs.map(function(c, j) {
      return (
        // Cabeçalho do lado da coluna
        <tr key={`colAttr${j}`}>
          {/* Espaços em branco reservado para Linhas */}
          {j === 0 && rowAttrs.length !== 0 && (
            <th colSpan={rowAttrs.length} rowSpan={colAttrs.length} />
          )}

          {/* Nome do atributo Coluna */}
          <th className="pvtAxisLabel">{c}</th>

          {/* Valores da coluna */}
          {colKeys.map(function(colKey, i) {
            const x = spanSize(colKeys, i, j); // Cálcula o tamanha da coluna
            if (x === -1) {
              return null;
            }
            return (
              <th
                className="pvtColLabel"
                key={`colKey${i}`}
                colSpan={x}
                rowSpan={
                  j === colAttrs.length - 1 && rowAttrs.length !== 0 ? 2 : 1
                }
              >
                {colKey[j]}
              </th>
            );
          })}

          {/* Totals: Coluna N, Linha 1 */}
          {j === 0 && (
            <th
              className="pvtTotalLabel"
              rowSpan={colAttrs.length + (rowAttrs.length === 0 ? 0 : 1)}
            >
              {i18n.getMessage('Totals')}
            </th>
          )}
        </tr>
      );
    })}

    {/* Cabeçalho do lado da linha */}
    {rowAttrs.length !== 0 && (
      <tr>
        {rowAttrs.map(function(r, i) {
          return (
            <th className="pvtAxisLabel" rowSpan={1} key={`rowAttr${i}`}>
              {r}
            </th>
          );
        })}
        {/* Variáveis apenas na linha */}
        <th className="pvtTotalLabel">
          {colAttrs.length === 0 ? i18n.getMessage('Totals') : null}
        </th>
      </tr>
    )}
  </thead>
);

const TBody = ({
  rowKeys,
  colKeys,
  pivotData,
  rowAttrs,
  colAttrs,
  getClickHandler,
  rowTotalColors,
  valueCellColors,
  colTotalColors,
  grandTotalAggregator,
}) => (
  <tbody>
    {rowKeys.map(function(rowKey, i) {
      const totalAggregator = pivotData.getAggregator(rowKey, []);
      return (
        <tr key={`rowKeyRow${i}`}>
          {rowKey.map(function(txt, j) {
            const x = spanSize(rowKeys, i, j);
            if (x === -1) {
              return null;
            }
            return (
              <th
                key={`rowKeyLabel${i}-${j}`}
                className="pvtRowLabel"
                rowSpan={x}
                colSpan={
                  j === rowAttrs.length - 1 && colAttrs.length !== 0 ? 2 : 1
                }
              >
                <div dangerouslySetInnerHTML={{__html: txt}} />
              </th>
            );
          })}
          {colKeys.map(function(colKey, j) {
            const aggregator = pivotData.getAggregator(rowKey, colKey);
            return (
              <td
                className="pvtVal"
                key={`pvtVal${i}-${j}`}
                onClick={
                  getClickHandler &&
                  getClickHandler(aggregator.value(), rowKey, colKey)
                }
                style={valueCellColors(rowKey, colKey, aggregator.value())}
                // Precisa fazer a lógica para exibir até dois
              >
                {aggregator.format(aggregator.value())}
              </td>
            );
          })}
          {/* Total coluna */}
          <td
            className="pvtTotal"
            onClick={
              getClickHandler &&
              getClickHandler(totalAggregator.value(), rowKey, [null])
            }
            style={colTotalColors(totalAggregator.value())}
          >
            {totalAggregator.format(totalAggregator.value())}
          </td>
        </tr>
      );
    })}

    {/* Última linha */}
    <tr>
      {/* Total Linha */}
      <th
        className="pvtTotalLabel"
        colSpan={rowAttrs.length + (colAttrs.length === 0 ? 0 : 1)}
      >
        {i18n.getMessage('Totals')}
      </th>

      {colKeys.map(function(colKey, i) {
        const totalAggregator = pivotData.getAggregator([], colKey);
        return (
          <td
            className="pvtTotal"
            key={`total${i}`}
            onClick={
              getClickHandler &&
              getClickHandler(totalAggregator.value(), [null], colKey)
            }
            style={rowTotalColors(totalAggregator.value())}
          >
            {totalAggregator.format(totalAggregator.value())}
          </td>
        );
      })}

      {/* Total cruzamento */}
      <td
        onClick={
          getClickHandler &&
          getClickHandler(grandTotalAggregator.value(), [null], [null])
        }
        className="pvtGrandTotal"
      >
        {grandTotalAggregator.format(grandTotalAggregator.value())}
      </td>
    </tr>
  </tbody>
);

function makeRenderer(opts = {}) {
  class TableRenderer extends React.PureComponent {
    componentDidUpdate() {
      const {enableRowNumbers} = this.props;
      if (enableRowNumbers) {
        makeNumbers();
      }
    }

    render() {
      const pivotData = new PivotData(this.props);
      const colAttrs = pivotData.props.cols;
      const rowAttrs = pivotData.props.rows;
      const rowKeys = pivotData.getRowKeys();
      const colKeys = pivotData.getColKeys();
      const grandTotalAggregator = pivotData.getAggregator([], []);
      const grandTotalAggregatorList = pivotData.getAggregatorList([], []);
      const [aggAttrs, aggTitles] = pivotData.getAggAttrs();
      const enablePercentage = !!pivotData.props.enablePercentage;
      let ITBody = TBody;
      let ITHead = THead;

      const condicaoComFuncaoAgregacao = pivotData.hasAggList();
      const condicaoComPorcentagem =
        enablePercentage && condicaoComFuncaoAgregacao;
      if (condicaoComPorcentagem) {
        ITBody = TBodyAggWithPer;
        ITHead = THeadAggWithPer;
      } else if (condicaoComFuncaoAgregacao) {
        ITBody = TBodyAgg;
        ITHead = THeadAgg;
      }
      /**
       * HEATMAP MODE
       */
      let valueCellColors = () => {};
      let rowTotalColors = () => {};
      let colTotalColors = () => {};
      if (opts.heatmapMode) {
        const colorScaleGenerator = this.props.tableColorScaleGenerator;
        const rowTotalValues = colKeys.map(x =>
          pivotData.getAggregator([], x).value()
        );
        rowTotalColors = colorScaleGenerator(rowTotalValues);
        const colTotalValues = rowKeys.map(x =>
          pivotData.getAggregator(x, []).value()
        );
        colTotalColors = colorScaleGenerator(colTotalValues);

        if (opts.heatmapMode === 'full') {
          const allValues = [];
          rowKeys.forEach(r =>
            colKeys.forEach(c =>
              allValues.push(pivotData.getAggregator(r, c).value())
            )
          );
          const colorScale = colorScaleGenerator(allValues);
          valueCellColors = (r, c, v) => colorScale(v);
        } else if (opts.heatmapMode === 'row') {
          const rowColorScales = {};
          rowKeys.forEach(r => {
            const rowValues = colKeys.map(x =>
              pivotData.getAggregator(r, x).value()
            );
            rowColorScales[r] = colorScaleGenerator(rowValues);
          });
          valueCellColors = (r, c, v) => rowColorScales[r](v);
        } else if (opts.heatmapMode === 'col') {
          const colColorScales = {};
          colKeys.forEach(c => {
            const colValues = rowKeys.map(x =>
              pivotData.getAggregator(x, c).value()
            );
            colColorScales[c] = colorScaleGenerator(colValues);
          });
          valueCellColors = (r, c, v) => colColorScales[c](v);
        }
      }

      /**
       * Evento Click
       */
      const getClickHandler =
        this.props.tableOptions && this.props.tableOptions.clickCallback
          ? (value, rowValues, colValues) => {
              const filters = {};
              for (const i of Object.keys(colAttrs || {})) {
                const attr = colAttrs[i];
                if (colValues[i] !== null) {
                  filters[attr] = colValues[i];
                }
              }
              for (const i of Object.keys(rowAttrs || {})) {
                const attr = rowAttrs[i];
                if (rowValues[i] !== null) {
                  filters[attr] = rowValues[i];
                }
              }
              return e =>
                this.props.tableOptions.clickCallback(
                  e,
                  value,
                  filters,
                  pivotData
                );
            }
          : null;

      return (
        <table className="pvtTable">
          {/* CABEÇALHO */}
          <ITHead
            colKeys={colKeys}
            rowAttrs={rowAttrs}
            colAttrs={colAttrs}
            aggAttrs={aggAttrs}
            aggTitles={aggTitles}
          />

          {/* CORPO */}
          <ITBody
            rowKeys={rowKeys}
            colKeys={colKeys}
            pivotData={pivotData}
            rowAttrs={rowAttrs}
            colAttrs={colAttrs}
            getClickHandler={getClickHandler}
            rowTotalColors={rowTotalColors}
            valueCellColors={valueCellColors}
            colTotalColors={colTotalColors}
            grandTotalAggregatorList={grandTotalAggregatorList}
            grandTotalAggregator={grandTotalAggregator}
          />
        </table>
      );
    }
  }

  TableRenderer.defaultProps = PivotData.defaultProps;
  TableRenderer.propTypes = PivotData.propTypes;
  TableRenderer.defaultProps.tableColorScaleGenerator = redColorScaleGenerator;
  TableRenderer.defaultProps.tableOptions = {};
  TableRenderer.propTypes.tableColorScaleGenerator = PropTypes.func;
  TableRenderer.propTypes.tableOptions = PropTypes.object;
  return TableRenderer;
}

class TSVExportRenderer extends React.PureComponent {
  render() {
    const pivotData = new PivotData(this.props);
    const rowKeys = pivotData.getRowKeys();
    const colKeys = pivotData.getColKeys();
    if (rowKeys.length === 0) {
      rowKeys.push([]);
    }
    if (colKeys.length === 0) {
      colKeys.push([]);
    }

    const headerRow = pivotData.props.rows.map(r => r);
    if (colKeys.length === 1 && colKeys[0].length === 0) {
      headerRow.push(this.props.aggregatorName);
    } else {
      colKeys.forEach(c => headerRow.push(c.join('-')));
    }

    const result = rowKeys.map(r => {
      const row = r.map(x => x);
      colKeys.forEach(c => {
        const v = pivotData.getAggregator(r, c).value();
        row.push(v ? v : '');
      });
      return row;
    });

    result.unshift(headerRow);

    return (
      <textarea
        value={result.map(r => r.join('\t')).join('\n')}
        style={{width: window.innerWidth / 2, height: window.innerHeight / 2}}
        readOnly={true}
      />
    );
  }
}

TSVExportRenderer.defaultProps = PivotData.defaultProps;
TSVExportRenderer.propTypes = PivotData.propTypes;

export default {
  Table: makeRenderer(),
  'Table Heatmap': makeRenderer({heatmapMode: 'full'}),
  'Table Col Heatmap': makeRenderer({heatmapMode: 'col'}),
  'Table Row Heatmap': makeRenderer({heatmapMode: 'row'}),
  'Exportable TSV': TSVExportRenderer,
};
